import axios, { AxiosError } from 'axios';
import type { LoginCredentials, AuthResponse, User, Schedule, Reservation, Task } from '../types';

// Determine API base URL
// If REACT_APP_API_URL is empty string or not set, use relative URLs (same domain)
// This avoids CORS issues when frontend and backend are on the same domain
let API_BASE_URL = process.env.REACT_APP_API_URL || '';
if (!API_BASE_URL || API_BASE_URL === 'http://localhost:8000') {
    // In production, if API URL is localhost or empty, use relative URLs
    // This means requests will go to the same domain where React is served
    API_BASE_URL = '';
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Debug logging for API calls
if (process.env.NODE_ENV === 'development' || window.location.hostname !== 'localhost') {
  api.interceptors.request.use(
    (config) => {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.baseURL || ''}${config.url}`, {
        data: config.data,
        headers: config.headers,
      });
      return config;
    },
    (error) => {
      console.error('[API Request Error]', error);
      return Promise.reject(error);
    }
  );

  api.interceptors.response.use(
    (response) => {
      console.log(`[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`, {
        status: response.status,
        data: response.data,
      });
      return response;
    },
    (error) => {
      console.error(`[API Response Error] ${error.config?.method?.toUpperCase()} ${error.config?.url}`, {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      return Promise.reject(error);
    }
  );
}

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Token ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (credentials: LoginCredentials, adminAccessToken?: string): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/api/auth/login/', credentials, {
      headers: adminAccessToken ? { 'X-Admin-Access-Token': adminAccessToken } : undefined,
    });
    return response.data;
  },

  googleLogin: async (credentialOrCode: string, isCode: boolean = false, redirectUri?: string): Promise<AuthResponse & { has_calendar_access?: boolean }> => {
    const payload = isCode 
      ? { 
          code: credentialOrCode,
          redirect_uri: redirectUri || window.location.origin + '/login'
        }
      : { access_token: credentialOrCode };
    
    const response = await api.post<AuthResponse & { has_calendar_access?: boolean }>('/api/auth/google-login/', payload);
    return response.data;
  },

  logout: async (): Promise<void> => {
    await api.post('/api/auth/logout/');
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await api.get<User>('/api/auth/me/');
    return response.data;
  },

  updateWorkingHours: async (workingHours: any): Promise<User> => {
    const response = await api.post<User>('/api/auth/working-hours/', {
      working_hours: workingHours,
    });
    return response.data;
  },

  toggleWorkingHourException: async (date: string, time: string): Promise<any> => {
    const response = await api.post('/api/auth/working-hours/toggle/', {
      date,
      time,
    });
    return response.data;
  },

  updateSlackUserId: async (slackUserId: string): Promise<User> => {
    const response = await api.post<User>('/api/auth/slack-user-id/', {
      slack_user_id: slackUserId,
    });
    return response.data;
  },

  syncGoogleCalendar: async (): Promise<any> => {
    const response = await api.post('/api/auth/sync-google-calendar/');
    return response.data;
  },

  connectGoogleCalendar: async (code: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post<{ success: boolean; message: string }>(
      '/api/auth/connect-google-calendar/',
      { code }
    );
    return response.data;
  },

  checkPasswordLoginEnabled: async (adminAccessToken?: string): Promise<boolean> => {
    try {
      const response = await api.get<{ enabled: boolean; message?: string }>('/api/auth/password-login-enabled/', {
        headers: adminAccessToken ? { 'X-Admin-Access-Token': adminAccessToken } : undefined,
      });
      return response.data.enabled === true;
    } catch (error) {
      // If 404, password login is disabled
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          return false;
        }
        // For network errors (504, ECONNREFUSED, etc.), assume backend is down
        // Allow password login to be shown, but it will fail when trying to login
        // This allows user to at least see the login page
        if (error.code === 'ECONNREFUSED' || error.code === 'ETIMEDOUT' || 
            error.response?.status === 504 || error.response?.status === 502) {
          console.warn('Backend appears to be unavailable, assuming password login enabled for UI');
          return true; // Show login page, but login will fail if backend is down
        }
      }
      // For other errors, assume disabled to be safe
      return false;
    }
  },
};

// Schedules API
export const schedulesAPI = {
  getAll: async (): Promise<Schedule[]> => {
    const response = await api.get<Schedule[]>('/api/schedules/');
    return response.data;
  },

  getById: async (id: number): Promise<Schedule> => {
    const response = await api.get<Schedule>(`/api/schedules/${id}/`);
    return response.data;
  },

  create: async (data: Partial<Schedule>): Promise<Schedule> => {
    const response = await api.post<Schedule>('/api/schedules/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<Schedule>): Promise<Schedule> => {
    const response = await api.patch<Schedule>(`/api/schedules/${id}/`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/api/schedules/${id}/`);
  },

  getAvailableSlots: async (id: number): Promise<any> => {
    const response = await api.get(`/api/schedules/${id}/available_slots/`);
    return response.data;
  },

  updateWorkingHours: async (id: number, workingHours: any): Promise<Schedule> => {
    const response = await api.post<Schedule>(`/api/schedules/${id}/update_working_hours/`, {
      working_hours: workingHours,
    });
    return response.data;
  },
};

// Reservations API
export const reservationsAPI = {
  getAll: async (): Promise<Reservation[]> => {
    const response = await api.get<Reservation[]>('/api/reservations/');
    return response.data;
  },

  getUpcoming: async (): Promise<Reservation[]> => {
    const response = await api.get<Reservation[]>('/api/reservations/upcoming/');
    return response.data;
  },

  getById: async (id: number): Promise<Reservation> => {
    const response = await api.get<Reservation>(`/api/reservations/${id}/`);
    return response.data;
  },

  create: async (data: Partial<Reservation>): Promise<Reservation> => {
    const response = await api.post<Reservation>('/api/reservations/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<Reservation>): Promise<Reservation> => {
    const response = await api.patch<Reservation>(`/api/reservations/${id}/`, data);
    return response.data;
  },

  cancel: async (id: number): Promise<Reservation> => {
    const response = await api.post<Reservation>(`/api/reservations/${id}/cancel/`);
    return response.data;
  },
};

// Tasks API (new unified API)
export const tasksAPI = {
  getAll: async (params?: { include_blocked_time?: boolean }): Promise<Task[]> => {
    const queryParams: any = {};
    if (params?.include_blocked_time) {
      queryParams.include_blocked_time = 'true';
    }
    const response = await api.get<Task[]>('/api/tasks/', { params: queryParams });
    return response.data;
  },

  getUpcoming: async (): Promise<Task[]> => {
    const response = await api.get<Task[]>('/api/tasks/upcoming/');
    return response.data;
  },

  getFiltered: async (params: {
    period?: string;
    page?: number;
    page_size?: number;
    search?: string;
    status?: string;
    source?: string;
    ordering?: string;
  } = {}): Promise<{
    tasks: Task[];
    total_count: number;
    page: number;
    page_size: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
    period: string;
    start_date: string;
    end_date: string;
  }> => {
    const response = await api.get('/api/tasks/filtered/', { params });
    return response.data;
  },

  getById: async (id: number): Promise<Task> => {
    const response = await api.get<Task>(`/api/tasks/${id}/`);
    return response.data;
  },

  create: async (data: Partial<Task>): Promise<Task> => {
    const response = await api.post<Task>('/api/tasks/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<Task>): Promise<Task> => {
    const response = await api.patch<Task>(`/api/tasks/${id}/`, data);
    return response.data;
  },

  cancel: async (id: number): Promise<Task> => {
    const response = await api.post<Task>(`/api/tasks/${id}/cancel/`);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/api/tasks/${id}/`);
  },

  sendReminder: async (id: number): Promise<Task> => {
    const response = await api.post<Task>(`/api/tasks/${id}/send_reminder/`);
    return response.data;
  },

  recreateGoogleMeet: async (id: number): Promise<{ message: string; google_meet_link: string; task_id: number }> => {
    const response = await api.post<{ message: string; google_meet_link: string; task_id: number }>(`/api/tasks/${id}/recreate_google_meet/`);
    return response.data;
  },

  getLogs: async (id: number, format: 'json' | 'raw' = 'json'): Promise<{
    logs: Array<{
      timestamp: string;
      level: string;
      message: string;
      details?: any;
    }>;
    summary: {
      total: number;
      by_level: { [key: string]: number };
      latest: any;
    };
    task_id: number;
    client_email: string;
    client_name: string;
  } | string> => {
    const response = await api.get(`/api/tasks/${id}/logs/`, {
      params: format === 'raw' ? { output: 'raw' } : {},
      ...(format === 'raw' ? { responseType: 'text' } : {})
    });
    
    if (format === 'raw') {
      // For raw format, response.data is a string
      return response.data as string;
    } else {
      return response.data;
    }
  },
};

// Health Check API
export const healthAPI = {
  check: async (): Promise<{ status: string; timestamp: string }> => {
    const response = await api.get('/api/health/');
    return response.data;
  },
};

// Clients API
export const clientsAPI = {
  getAll: async (params?: {
    search?: string;
    page?: number;
    page_size?: number;
    ordering?: string;
  }): Promise<any> => {
    const response = await api.get('/api/clients/', { params });
    return response.data;
  },

  getStats: async (): Promise<any> => {
    const response = await api.get('/api/clients/stats/');
    return response.data;
  },

  updateEmail: async (id: number, email: string): Promise<{
    message: string;
    client_id: number;
    old_email: string;
    new_email: string;
    tasks_updated: number;
    submissions_updated: number;
    client: any;
  }> => {
    const response = await api.patch(`/api/clients/${id}/update_email/`, { email });
    return response.data;
  },

  updateEmailByEmail: async (oldEmail: string, newEmail: string): Promise<{
    message: string;
    client_id: number;
    old_email: string;
    new_email: string;
    tasks_updated: number;
    submissions_updated: number;
    client: any;
  }> => {
    const response = await api.patch('/api/clients/update_email_by_email/', {
      old_email: oldEmail,
      new_email: newEmail,
    });
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/api/clients/${id}/`);
  },
};

// Webhook Submissions API
export interface WebhookSubmission {
  source_display?: string;
  id: number;
  email: string | null;
  raw_json: any;
  hubspot_status: 'pending' | 'success' | 'failed' | 'not_sent' | 'skipped' | 'error' | 'form_completed';
  hubspot_contact_id: string | null;
  hubspot_error: string | null;
  created_at: string;
  updated_at: string;
  source: string;
  processed: boolean;
  google_meet_id?: string | null;
  book_call_id?: number | null;
  assigned_manager?: number | null;
  assigned_manager_name?: string | null;
  assigned_manager_email?: string | null;
  form_stage?: string | null;
}

export const submissionsAPI = {
  getAll: async (params?: {
    search?: string;
    status?: string;
    source?: string;
    manager?: string;
    page?: number;
    page_size?: number;
    ordering?: string;
  }): Promise<{
    count: number;
    next: string | null;
    previous: string | null;
    results: WebhookSubmission[];
  }> => {
    const response = await api.get<{
      count: number;
      next: string | null;
      previous: string | null;
      results: WebhookSubmission[];
    }>('/api/submissions/', { params });
    return response.data;
  },

  getById: async (id: number): Promise<WebhookSubmission> => {
    const response = await api.get<WebhookSubmission>(`/api/submissions/${id}/`);
    return response.data;
  },

  updateJson: async (id: number, raw_json: any): Promise<WebhookSubmission> => {
    const response = await api.patch<WebhookSubmission>(`/api/submissions/${id}/update_json/`, {
      raw_json,
    });
    return response.data;
  },

  resendToHubspot: async (id: number): Promise<{
    success: boolean;
    message: string;
    hubspot_contact_id?: string;
    error?: string;
    submission: WebhookSubmission;
  }> => {
    const response = await api.post(`/api/submissions/${id}/resend_to_hubspot/`);
    return response.data;
  },

  getLogs: async (id: number, format: 'json' | 'raw' = 'json'): Promise<{
    logs: Array<{
      timestamp: string;
      level: string;
      message: string;
      details?: any;
    }>;
    summary: {
      total: number;
      by_level: { [key: string]: number };
      latest: any;
    };
    submission_id: number;
    email: string | null;
  } | string> => {
    const response = await api.get(`/api/submissions/${id}/logs/`, {
      params: format === 'raw' ? { output: 'raw' } : {},
      ...(format === 'raw' ? { responseType: 'text' } : {})
    });
    
    if (format === 'raw') {
      // For raw format, response.data is a string
      return response.data as string;
    } else {
      return response.data;
    }
  },

  getStats: async (period: 'week' | 'month' | '3months' | '6months' | 'year' = 'week'): Promise<{
    period: string;
    start_date: string;
    end_date: string;
    total_count: number;
    manager_stats: Array<{
      manager_id: number;
      manager_name: string;
      manager_email: string;
      count: number;
    }>;
    unassigned_count: number;
  }> => {
    const response = await api.get(`/api/submissions/stats/`, {
      params: { period }
    });
    return response.data;
  },

  getQueueStats: async (
    filter: { period: 'week' | 'month' | '3months' | '6months' | 'year' } | { startDate: string; endDate: string }
  ): Promise<{
    period: string;
    start_date: string;
    end_date: string;
    queue_split_date: string;
    manager_stats: Array<{
      manager_id: number;
      manager_name: string;
      manager_email: string;
      website_queue_count: number;
      third_party_queue_count: number;
    }>;
    totals: {
      website_queue_count: number;
      third_party_queue_count: number;
    };
  }> => {
    const params = 'startDate' in filter
      ? { start_date: filter.startDate, end_date: filter.endDate }
      : { period: filter.period };
    const response = await api.get(`/api/submissions/queue-stats/`, { params });
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/api/submissions/${id}/`);
  },

  exportData: async (startDate: string, endDate: string, format: 'csv' | 'excel'): Promise<{
    file_id: string;
    filename: string;
    download_url: string;
  }> => {
    const response = await api.post('/api/submissions/export/', {
      start_date: startDate,
      end_date: endDate,
      format: format,
    });
    return response.data;
  },

  downloadExport: async (fileId: string, filename: string): Promise<Blob> => {
    const response = await api.get(`/api/submissions/export/download/?file_id=${fileId}`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

// Users API
export const usersAPI = {
  getAll: async (): Promise<User[]> => {
    const response = await api.get<{ results: User[] }>('/api/users/');
    return response.data.results || response.data;
  },

  getById: async (id: number): Promise<User> => {
    const response = await api.get<User>(`/api/users/${id}/`);
    return response.data;
  },

  create: async (data: {
    email: string;
    username?: string;
    first_name?: string;
    last_name?: string;
    role: 'manager' | 'admin' | 'owner';
    password?: string;
    password_confirm?: string;
    phone?: string;
    used_for_autotests?: boolean;
  }): Promise<User> => {
    const response = await api.post<User>('/api/users/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<User>): Promise<User> => {
    const response = await api.patch<User>(`/api/users/${id}/`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/api/users/${id}/`);
  },
};

// Logs API
export interface LogEntry {
  content: string;
  has_content: boolean;
}

export interface LogsResponse {
  logs: LogEntry[];
  total_lines: number | null;
  total_lines_exact: boolean;
  retrieved_lines: number;
  search_applied: boolean;
  scan_truncated: boolean;
  scanned_files: string[];
  scanned_bytes: number;
  file_path?: string;
}

export const logsAPI = {
  getLogs: async (params?: {
    lines?: number;
    search?: string;
  }): Promise<LogsResponse> => {
    const response = await api.get<LogsResponse>('/api/logs/', { params });
    return response.data;
  },

  clearLogs: async (): Promise<{ success: boolean; message?: string; error?: string }> => {
    const response = await api.delete('/api/logs/clear/');
    return response.data;
  },

  getLogFiles: async (): Promise<{
    files: Array<{
      name: string;
      path: string;
      size: number;
      size_formatted: string;
      modified_date: string;
      modified_date_formatted: string;
    }>;
    logs_directory: string;
  }> => {
    const response = await api.get('/api/logs/files/');
    return response.data;
  },

  downloadLogFiles: async (fileNames: string[]): Promise<Blob> => {
    const response = await api.post('/api/logs/download/', 
      { files: fileNames },
      { responseType: 'blob' }
    );
    return response.data;
  },

  downloadLogsBackup: async (): Promise<Blob> => {
    const response = await api.get('/api/logs/backup/download/', 
      { responseType: 'blob' }
    );
    return response.data;
  },
};

export default api;
